/**
 * WCC360 — Career Intelligence v3.2 page sections.
 *
 * Presentation only. Every value is rendered verbatim from the sealed
 * workbook projection (`src/lib/careerIntel.ts`); a field with no evidence
 * renders nothing at all rather than a placeholder.
 */
import { Link } from "react-router-dom";
import EmployerLink from "@/components/career/EmployerLink";
import { ArrowRight, Building2, GraduationCap, Leaf, Radar, Sparkles } from "lucide-react";
import {
  DEMAND_YEARS, demandLevel, demandTone, forecastSentence, type CareerIntel,
} from "@/lib/careerIntel";

/* ---------------- small pieces ---------------- */

function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-3xl border border-border bg-card p-5 md:p-6 ${className}`}>
      {children}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{children}</div>
  );
}

function Chips({ items, tone = "default" }: { items: string[]; tone?: "default" | "primary" }) {
  if (!items.length) return null;
  const cls =
    tone === "primary"
      ? "border-primary/30 bg-primary/10 text-primary"
      : "border-border bg-secondary/60 text-foreground/90";
  return (
    <ul className="flex flex-wrap gap-2">
      {items.map((i) => (
        <li key={i} className={`text-[13px] px-3 py-1.5 rounded-full border ${cls}`}>
          {i}
        </li>
      ))}
    </ul>
  );
}

function Bullets({ items }: { items: string[] }) {
  if (!items.length) return null;
  return (
    <ul className="space-y-2.5">
      {items.map((i) => (
        <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-foreground/90">
          <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
          <span>{i}</span>
        </li>
      ))}
    </ul>
  );
}

function Meter({ label, value, max = 10 }: { label: string; value: number; max?: number }) {
  // Some sources store these on a 0–10 scale, others on 0–100. Normalise to /10
  // so the readout and the bar always agree.
  const scale = value > max ? 100 : max;
  const outOfTen = Math.round((value / scale) * 100) / 10;
  const shown = Number.isInteger(outOfTen) ? outOfTen : outOfTen.toFixed(1);
  const pct = Math.max(4, Math.min(100, (value / scale) * 100));
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <Label>{label}</Label>
        <span className="text-sm tabular-nums text-foreground/90">
          {shown}
          <span className="text-muted-foreground">/10</span>
        </span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/* ---------------- 1 · forecast ---------------- */

/**
 * The market demand outlook: four sealed bands (2026 → 2040) read as a
 * trajectory, not four unrelated numbers.
 */
export function DemandForecast({ intel }: { intel: CareerIntel }) {
  const points = DEMAND_YEARS.map((y) => ({ year: y, band: intel.demand[y] })).filter(
    (p) => p.band,
  );
  if (!points.length) return null;
  const sentence = forecastSentence(intel);

  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <div>
          <Label>Market demand outlook</Label>
          <p className="mt-1 font-display text-xl md:text-2xl font-medium">
            {intel.demand["2040"] ?? points[points.length - 1].band} by 2040
          </p>
        </div>
        {intel.growthPct != null && (
          <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 text-emerald-300">
            <Radar className="h-3.5 w-3.5" aria-hidden />
            {Math.round(intel.growthPct)}% growth
          </span>
        )}
      </div>

      <ol
        className="mt-6 grid gap-3"
        style={{ gridTemplateColumns: `repeat(${points.length}, minmax(0, 1fr))` }}
      >
        {points.map((p) => {
          const level = demandLevel(p.band) ?? 20;
          return (
            <li key={p.year} className="flex flex-col items-center gap-2">
              <div
                className="w-full h-28 rounded-2xl bg-secondary/60 flex items-end overflow-hidden"
                role="img"
                aria-label={`${p.year}: ${p.band} demand`}
              >
                <div
                  className={`w-full rounded-2xl ${demandTone(p.band)}`}
                  style={{ height: `${level}%` }}
                />
              </div>
              <span className="text-sm font-medium tabular-nums">{p.year}</span>
              <span className="text-[11px] text-center leading-tight text-muted-foreground">
                {p.band}
              </span>
            </li>
          );
        })}
      </ol>

      {sentence && (
        <p className="mt-5 text-[15px] leading-relaxed text-foreground/90">{sentence}</p>
      )}
      {intel.demandBasis && (
        <p className="mt-2 text-xs text-muted-foreground">Basis: {intel.demandBasis}</p>
      )}
    </Card>
  );
}

/* ---------------- 2 · pay + conditions ---------------- */

export function PayAndConditions({ intel }: { intel: CareerIntel }) {
  const hasSalary = Boolean(intel.salaryStart || intel.salaryMid);
  const facts = [
    intel.remote ? { label: "Remote", value: intel.remote } : null,
    intel.freelance ? { label: "Freelance", value: intel.freelance } : null,
    intel.entrepreneurship ? { label: "Starting your own", value: intel.entrepreneurship } : null,
    intel.environment ? { label: "Typical environment", value: intel.environment } : null,
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  if (!hasSalary && !facts.length && intel.workLifeBalance == null) return null;

  return (
    <div className="space-y-4">
      {hasSalary && (
        <Card>
          <Label>Salary bands (India)</Label>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {intel.salaryStart && (
              <div className="rounded-2xl border border-border bg-secondary/40 p-4">
                <div className="text-xs text-muted-foreground">Starting out</div>
                <div className="mt-1 font-display text-xl md:text-2xl font-medium tabular-nums">
                  {intel.salaryStart}
                </div>
              </div>
            )}
            {intel.salaryMid && (
              <div className="rounded-2xl border border-border bg-secondary/40 p-4">
                <div className="text-xs text-muted-foreground">Mid-career</div>
                <div className="mt-1 font-display text-xl md:text-2xl font-medium tabular-nums">
                  {intel.salaryMid}
                </div>
              </div>
            )}
          </div>
          {intel.salarySource && (
            <p className="mt-3 text-xs text-muted-foreground">Source: {intel.salarySource}</p>
          )}
        </Card>
      )}

      {(intel.workLifeBalance != null || intel.jobSatisfaction != null) && (
        <Card className="grid gap-5 sm:grid-cols-2">
          {intel.workLifeBalance != null && (
            <Meter label="Work-life balance" value={Math.round(intel.workLifeBalance)} />
          )}
          {intel.jobSatisfaction != null && (
            <Meter label="Job satisfaction" value={Math.round(intel.jobSatisfaction)} />
          )}
        </Card>
      )}

      {facts.length > 0 && (
        <Card className="divide-y divide-border p-0">
          {facts.map((f) => (
            <div key={f.label} className="p-5 md:p-6">
              <Label>{f.label}</Label>
              <p className="mt-1.5 text-[15px] leading-relaxed text-foreground/90">{f.value}</p>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

/* ---------------- 3 · AI and automation ---------------- */

export function AutomationOutlook({ intel }: { intel: CareerIntel }) {
  const a = intel.automation;
  const any =
    a.exposure != null || a.tier || a.effect || a.horizon || a.note ||
    intel.humanAdvantage || intel.aiResilience != null || intel.futureReadiness != null;
  if (!any) return null;

  const stats = [
    a.exposure != null ? { label: "Automation exposure", value: Math.round(a.exposure) } : null,
    intel.aiResilience != null ? { label: "AI resilience", value: Math.round(intel.aiResilience) } : null,
    intel.futureReadiness != null
      ? { label: "Future readiness", value: Math.round(intel.futureReadiness) }
      : null,
  ].filter(Boolean) as Array<{ label: string; value: number }>;

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2">
        {a.tier && (
          <span className="text-xs font-medium px-2.5 py-1 rounded-full border border-border bg-secondary/60">
            {a.tier} exposure
          </span>
        )}
        {a.effect && (
          <span className="text-xs font-medium px-2.5 py-1 rounded-full border border-border bg-secondary/60">
            {a.effect}
          </span>
        )}
        {a.horizon && (
          <span className="text-xs font-medium px-2.5 py-1 rounded-full border border-border bg-secondary/60">
            {a.horizon}
          </span>
        )}
      </div>

      {stats.length > 0 && (
        <div className="mt-5 grid gap-5 sm:grid-cols-3">
          {stats.map((s) => (
            <Meter key={s.label} label={s.label} value={s.value} max={100} />
          ))}
        </div>
      )}

      {a.note && (
        <p className="mt-5 text-[15px] leading-relaxed text-foreground/90">{a.note}</p>
      )}
      {intel.humanAdvantage && (
        <div className="mt-4 border-t border-border pt-4">
          <Label>What stays human</Label>
          <p className="mt-1.5 text-[15px] leading-relaxed text-foreground/90">
            {intel.humanAdvantage}
          </p>
        </div>
      )}
      {intel.futureSkills.length > 0 && (
        <div className="mt-4 border-t border-border pt-4">
          <Label>Skills to build now</Label>
          <div className="mt-3">
            <Chips items={intel.futureSkills} tone="primary" />
          </div>
        </div>
      )}
    </Card>
  );
}

/* ---------------- 4 · the work itself ---------------- */

export function TheWork({ intel }: { intel: CareerIntel }) {
  if (!intel.responsibilities.length && !intel.mustHave.length && !intel.niceToHave.length)
    return null;
  return (
    <div className="space-y-4">
      {intel.responsibilities.length > 0 && (
        <Card>
          <Label>Key responsibilities</Label>
          <div className="mt-4">
            <Bullets items={intel.responsibilities} />
          </div>
        </Card>
      )}
      {intel.mustHave.length > 0 && (
        <Card>
          <Label>Must-have skills</Label>
          <div className="mt-4">
            <Chips items={intel.mustHave} tone="primary" />
          </div>
        </Card>
      )}
      {intel.niceToHave.length > 0 && (
        <Card>
          <Label>Nice-to-have skills</Label>
          <div className="mt-4">
            <Chips items={intel.niceToHave} />
          </div>
        </Card>
      )}
    </div>
  );
}

/* ---------------- 5 · getting in ---------------- */

export function GettingIn({ intel }: { intel: CareerIntel }) {
  if (!intel.entry && !intel.subjects.length && !intel.pathways.length && !intel.employers.length)
    return null;
  return (
    <div className="space-y-4">
      {(intel.entry || intel.subjects.length > 0) && (
        <Card>
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-primary" aria-hidden />
            <Label>Entry requirements</Label>
          </div>
          {intel.entry && (
            <p className="mt-3 text-[15px] leading-relaxed text-foreground/90">{intel.entry}</p>
          )}
          {intel.subjects.length > 0 && (
            <div className="mt-4 border-t border-border pt-4">
              <Label>Subjects that help</Label>
              <div className="mt-3">
                <Chips items={intel.subjects} />
              </div>
            </div>
          )}
        </Card>
      )}
      {intel.pathways.length > 0 && (
        <Card>
          <Label>Where people study this</Label>
          <div className="mt-3">
            <Chips items={intel.pathways} />
          </div>
        </Card>
      )}
      {intel.employers.length > 0 && (
        <Card>
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" aria-hidden />
            <Label>Who hires for this</Label>
          </div>
          <ul className="mt-3 flex flex-wrap gap-2">
            {intel.employers.map((e) => (
              <li key={e}>
                <EmployerLink
                  company={e}
                  kind="company"
                  surface="career_intel_getting_in"
                  className="inline-flex min-h-[32px] items-center gap-1 rounded-full border border-border bg-secondary/60 px-3 py-1.5 text-[13px] text-foreground/90 underline-offset-4 transition-colors hover:border-primary/40 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

/* ---------------- 6 · who thrives ---------------- */

export function WhoThrives({ intel }: { intel: CareerIntel }) {
  const blocks = [
    { label: "Interests", items: intel.interests },
    { label: "Strengths", items: intel.strengths },
    { label: "Motivators", items: intel.motivators },
    { label: "Values", items: intel.values },
  ].filter((b) => b.items.length);
  const lines = [
    intel.learningStyle ? { label: "Learns best by", value: intel.learningStyle } : null,
    intel.workingStyle ? { label: "Works best", value: intel.workingStyle } : null,
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  if (!blocks.length && !lines.length) return null;

  return (
    <Card className="space-y-5">
      {blocks.map((b) => (
        <div key={b.label}>
          <Label>{b.label}</Label>
          <div className="mt-3">
            <Chips items={b.items} />
          </div>
        </div>
      ))}
      {lines.map((l) => (
        <div key={l.label} className="border-t border-border pt-4">
          <Label>{l.label}</Label>
          <p className="mt-1.5 text-[15px] leading-relaxed text-foreground/90">{l.value}</p>
        </div>
      ))}
    </Card>
  );
}

/* ---------------- 7 · impact ---------------- */

export function ImpactPanel({ intel }: { intel: CareerIntel }) {
  if (!intel.socialImpact && !intel.environmentalImpact) return null;
  return (
    <Card className="space-y-4">
      {intel.socialImpact && (
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" aria-hidden />
            <Label>Social impact</Label>
          </div>
          <p className="mt-2 text-[15px] leading-relaxed text-foreground/90">
            {intel.socialImpact}
          </p>
        </div>
      )}
      {intel.environmentalImpact && (
        <div className={intel.socialImpact ? "border-t border-border pt-4" : ""}>
          <div className="flex items-center gap-2">
            <Leaf className="h-4 w-4 text-primary" aria-hidden />
            <Label>Environmental impact</Label>
          </div>
          <p className="mt-2 text-[15px] leading-relaxed text-foreground/90">
            {intel.environmentalImpact}
          </p>
        </div>
      )}
    </Card>
  );
}

/* ---------------- 8 · moves from here ---------------- */

export function MovesFromHere({
  intel,
  resolve,
  from,
}: {
  intel: CareerIntel;
  /** Career name → ci_id, so a related role links to its own full page. */
  resolve: (name: string) => string | null;
  from: string;
}) {
  const groups = [
    { label: "Adjacent roles", hint: "Close to this one — similar skills, different focus.", items: intel.adjacent },
    { label: "Stretch roles", hint: "A step up or sideways, once you have some ground under you.", items: intel.stretch },
  ].filter((g) => g.items.length);
  if (!groups.length) return null;

  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <Card key={g.label} className="p-0">
          <div className="p-5 md:p-6 pb-3">
            <Label>{g.label}</Label>
            <p className="mt-1 text-sm text-muted-foreground">{g.hint}</p>
          </div>
          <ul className="divide-y divide-border border-t border-border">
            {g.items.map((name) => {
              const ciId = resolve(name);
              return (
                <li key={name}>
                  {ciId ? (
                    <Link
                      to={`/careers/${ciId}`}
                      state={{ from }}
                      className="flex items-center justify-between gap-3 p-4 md:px-6 min-h-[56px]
                        hover:bg-secondary/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <span className="font-medium">{name}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
                    </Link>
                  ) : (
                    <div className="p-4 md:px-6 min-h-[56px] flex items-center text-foreground/80">
                      {name}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      ))}
    </div>
  );
}

/* ---------------- 9 · provenance ---------------- */

export function IntelProvenance({ intel }: { intel: CareerIntel }) {
  const bits = [
    intel.wk,
    intel.iscoGroup ? `NCO-2015 / ISCO-08: ${intel.iscoGroup}` : null,
    intel.evidence,
    intel.confidence != null ? `Confidence ${Math.round(intel.confidence)}/100` : null,
    intel.lastUpdated ? `Updated ${intel.lastUpdated}` : null,
  ].filter(Boolean) as string[];
  if (!bits.length) return null;
  return (
    <p className="mt-6 text-xs text-muted-foreground leading-relaxed">
      Career Intelligence v3.2 · {bits.join(" · ")}
      {intel.provenance ? ` · ${intel.provenance}` : ""}
    </p>
  );
}
